import { Directory, Filesystem } from '@capacitor/filesystem';
import { isPlatform } from '../../utils/isPlatform';
import { arrayBufferToBase64, base64ToArrayBuffer } from '../../utils/Audio';
import { IRecordingStorageProvider } from './IRecordingStorageProvider';
import { IRecording } from '../RecordingState';
import { Logger } from '../../support/Logger';
import { DevSettings } from '../../support/DevSettings';

const RECORDINGS_DIR = 'recordings';

/**
 * Metadata stored in JSON file (everything except ArrayBuffer).
 */
type IRecordingMetadata = Omit<IRecording, 'file'>;

/**
 * Filesystem-based recording storage provider.
 * Uses Capacitor Filesystem for native platforms only.
 *
 * Storage structure:
 * - recordings/{id}.json  - Recording metadata (without file ArrayBuffer)
 * - recordings/{id}.audio - Audio data as base64 string
 */
export class FilesystemRecordingProvider implements IRecordingStorageProvider<IRecording> {
    static inject = () => [Logger, DevSettings];

    constructor(
        private logger: Logger,
        private devSettings: DevSettings
    ) {}

    async add(data: IRecording): Promise<string> {
        await this.ensureDirectoryExists();

        // Separate metadata from audio data
        const { file, ...metadata } = data;

        // Write metadata JSON
        await Filesystem.writeFile({
            path: `${RECORDINGS_DIR}/${data.id}.json`,
            data: JSON.stringify(metadata),
            directory: Directory.Data,
            encoding: 'utf8' as any
        });

        // Write audio data as base64
        const audioBase64 = arrayBufferToBase64(file);
        await Filesystem.writeFile({
            path: `${RECORDINGS_DIR}/${data.id}.audio`,
            data: audioBase64,
            directory: Directory.Data,
            encoding: 'utf8' as any
        });

        return data.id;
    }

    async get(id: string): Promise<IRecording | undefined> {
        try {
            // Read metadata
            const metadataResult = await Filesystem.readFile({
                path: `${RECORDINGS_DIR}/${id}.json`,
                directory: Directory.Data,
                encoding: 'utf8' as any
            });
            const metadata: IRecordingMetadata = JSON.parse(metadataResult.data as string);

            // Read audio data
            const audioResult = await Filesystem.readFile({
                path: `${RECORDINGS_DIR}/${id}.audio`,
                directory: Directory.Data,
                encoding: 'utf8' as any
            });
            const file = base64ToArrayBuffer(audioResult.data as string);

            return { ...metadata, file };
        } catch (error: any) {
            // File not found is expected when record doesn't exist
            if (error?.message?.includes('not exist') || error?.message?.includes('not found')) {
                return undefined;
            }
            throw error;
        }
    }

    async getAll(): Promise<IRecording[]> {
        try {
            await this.ensureDirectoryExists();

            const dirResult = await Filesystem.readdir({
                path: RECORDINGS_DIR,
                directory: Directory.Data
            });

            // Get unique recording IDs from .json files
            const ids = dirResult.files
                .filter(f => f.name.endsWith('.json'))
                .map(f => f.name.replace('.json', ''));

            // Load each recording
            const recordings: IRecording[] = [];
            for (const id of ids) {
                const recording = await this.get(id);
                if (recording) {
                    recordings.push(recording);
                }
            }

            return recordings;
        } catch (error: any) {
            // Directory doesn't exist means no recordings
            if (error?.message?.includes('not exist') || error?.message?.includes('not found')) {
                return [];
            }
            throw error;
        }
    }

    async update(data: IRecording): Promise<void> {
        // Check if record exists first
        const existing = await this.get(data.id);
        if (!existing) {
            throw new Error(`Record with key ${data.id} not found`);
        }

        // Merge and write
        const merged = { ...existing, ...data };
        const { file, ...metadata } = merged;

        // Update metadata
        await Filesystem.writeFile({
            path: `${RECORDINGS_DIR}/${data.id}.json`,
            data: JSON.stringify(metadata),
            directory: Directory.Data,
            encoding: 'utf8' as any
        });

        // Update audio if provided
        if (data.file) {
            const audioBase64 = arrayBufferToBase64(file);
            await Filesystem.writeFile({
                path: `${RECORDINGS_DIR}/${data.id}.audio`,
                data: audioBase64,
                directory: Directory.Data,
                encoding: 'utf8' as any
            });
        }
    }

    async delete(id: string): Promise<void> {
        try {
            await Filesystem.deleteFile({
                path: `${RECORDINGS_DIR}/${id}.json`,
                directory: Directory.Data
            });
        } catch (error: any) {
            // Ignore if file doesn't exist
            if (!error?.message?.includes('not exist') && !error?.message?.includes('not found')) {
                throw error;
            }
        }

        try {
            await Filesystem.deleteFile({
                path: `${RECORDINGS_DIR}/${id}.audio`,
                directory: Directory.Data
            });
        } catch (error: any) {
            // Ignore if file doesn't exist
            if (!error?.message?.includes('not exist') && !error?.message?.includes('not found')) {
                throw error;
            }
        }

        this.logger.info(`Deleted recording ${id} from Filesystem`);
    }

    /**
     * Filesystem storage is only available on native platforms (iOS/Android).
     * Returns false on web.
     */
    async isAvailable(): Promise<boolean> {
        // Check dev flag for testing
        if (this.devSettings.simulateFilesystemFailure) {
            return false;
        }

        // Only available on native platforms
        if (!isPlatform('capacitor')) {
            return false;
        }

        // Test actual filesystem access
        try {
            await this.ensureDirectoryExists();
            return true;
        } catch (error) {
            // Only log to Sentry if not simulating failures (dev flag)
            if (!this.devSettings.simulateFilesystemFailure) {
                this.logger.error('Filesystem storage not available:', error);
            }
            return false;
        }
    }

    private async ensureDirectoryExists(): Promise<void> {
        try {
            await Filesystem.mkdir({
                path: RECORDINGS_DIR,
                directory: Directory.Data,
                recursive: true
            });
        } catch (error: any) {
            // Directory already exists is fine
            if (!error?.message?.includes('already exist')) {
                throw error;
            }
        }
    }
}
